import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { 
  Ticket, Clock, CheckCircle, AlertCircle, Plus, Search, BookOpen, Bell, Eye,
  FileText, Zap, Shield, Users, ArrowRight, MessageCircle
} from 'lucide-react';
import CreateTicketForm from './tickets/CreateTicketForm';
import MyTickets from './tickets/MyTickets';
import KnowledgeSearch from './KnowledgeSearch';
import KnowledgeBaseAccess from './knowledge-base/KnowledgeBaseAccess';
// Header and Footer are provided by PortalHomeLayout, no need to import here
import authService from '../../api/authService';
// import LandingChatbot from '../../components/chatbot/LandingChatbot';
import { ticketService } from '../../api/ticketService';
import toast from 'react-hot-toast';
import { isCurrentUserCustomer } from '../../utils/notificationNavigation';
import {
  getCustomerFacingTicketStatusLabel,
  getCustomerFacingTicketStatusStyleKey,
} from '../../utils/portalCustomerStatus';

const UnifiedPortalDashboard = () => {
  const navigate = useNavigate();
  const isCustomerViewer = isCurrentUserCustomer();
  
  // Ticket data state (from PortalDashboard)
  const [ticketOverview, setTicketOverview] = useState({
    total: 0,
    open: 0,
    resolved: 0,
    inProgress: 0,
    closed: 0
  });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalTicketsLoading, setModalTicketsLoading] = useState(false);
  
  // Modal states (from PortalDashboard)
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [showSearchKBModal, setShowSearchKBModal] = useState(false);
  /** Query from hero search — passed into KB modal so search runs once without retyping. */
  const [kbModalSeedQuery, setKbModalSeedQuery] = useState('');
  const [showMyTicketsModal, setShowMyTicketsModal] = useState(false);
  const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);

  // Search state (from PortalHome)
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal search and filter state
  const [ticketModalSearchQuery, setTicketModalSearchQuery] = useState('');
  const [ticketModalStatusFilter, setTicketModalStatusFilter] = useState('all');
  
  // Get user email for chatbot
  const [userEmail, setUserEmail] = useState(null);

  // Features data (from PortalHome)
  const features = [
    {
      title: "24/7 Support",
      description: "Get help anytime, anywhere. Our support team and knowledge base are always available.",
      icon: Clock,
      color: "text-info"
    },
    {
      title: "Quick Resolution",
      description: "Track your issues from submission to resolution with real-time updates.",
      icon: CheckCircle,
      color: "text-success"
    },
    {
      title: "Fast Response",
      description: "Average response time under 2 hours for all ticket priorities.",
      icon: Zap,
      color: "text-warning"
    }
  ];

  // How It Works data (from PortalHome)
  const howItWorks = [
    {
      step: "1",
      title: "Submit Your Request",
      description: "Create a ticket by describing your issue or question. Include relevant details and attachments.",
      icon: Plus
    },
    {
      step: "2",
      title: "We Review & Assign",
      description: "Our system automatically routes your ticket to the right team based on priority and category.",
      icon: Users
    },
    {
      step: "3",
      title: "Get Updates",
      description: "Receive real-time notifications via email and portal as our team works on your request.",
      icon: MessageCircle
    },
    {
      step: "4",
      title: "Issue Resolved",
      description: "Once resolved, review the solution and provide feedback to help us improve our service.",
      icon: CheckCircle
    }
  ];

  useEffect(() => {
    loadTicketOverview();
    const user = authService.getCurrentUser();
    if (user && user.email) {
      setUserEmail(user.email);
    }
  }, []);

  const transformTicket = (ticket) => {
    let status = 'open';
    if (ticket.status) {
      if (typeof ticket.status === 'string') {
        status = ticket.status.toLowerCase();
      } else if (ticket.status.statusName) {
        status = ticket.status.statusName.toLowerCase();
      } else if (ticket.status.statusCode) {
        status = ticket.status.statusCode.toLowerCase();
      }
    }
    return {
      id: ticket.ticketId || ticket.ticketNumber,
      ticketNumber: ticket.ticketNumber || ticket.ticketId || ticket.id,
      subject: ticket.subject,
      title: ticket.subject,
      description: ticket.description,
      status,
      statusCode: ticket.statusCode || null,
      statusId: ticket.statusId || null,
      priority: ticket.priorityName || ticket.priority || 'medium',
      category: ticket.categoryName || ticket.category || 'Uncategorized',
      createdAt: ticket.createdAt,
      updatedAt: ticket.updatedAt,
      assignedTo: ticket.assigneeFullName ? ticket.assigneeFullName : 'Unassigned',
      lastUpdate: ticket.updatedAt || ticket.createdAt,
      countryName: ticket.countryName,
      countyName: ticket.countyName,
      subCountyName: ticket.subCountyName,
      wardName: ticket.wardName
    };
  };

  const loadTicketOverview = async () => {
    try {
      setLoading(true);
      const response = await ticketService.getMyTicketsStatistics({ asRequester: true });
      const responseData = response?.data;
      const stats = responseData?.data;

      if (responseData?.success && stats) {
        setTicketOverview({
          total: Number(stats.total) || 0,
          open: Number(stats.open) || 0,
          resolved: Number(stats.resolved) || 0,
          inProgress: Number(stats['in-progress']) || 0,
          closed: Number(stats.closed) || 0
        });
      } else {
        setTicketOverview({ total: 0, open: 0, resolved: 0, inProgress: 0, closed: 0 });
      }
    } catch (err) {
      console.error('Error loading ticket overview:', err);
      toast.error('Failed to load ticket statistics');
      setTicketOverview({ total: 0, open: 0, resolved: 0, inProgress: 0, closed: 0 });
    } finally {
      setLoading(false);
    }
  };

  const getModalStatusParam = (type, statusFilter) => {
    if (type === 'open') return 'open';
    if (type === 'resolved') return 'resolved';
    if (type === 'closed') return 'closed';
    if (type === 'inProgress') return 'in-progress';
    if (type === 'total' && statusFilter && statusFilter !== 'all') {
      return statusFilter;
    }
    return undefined;
  };

  const loadModalTickets = async (type, statusFilter = ticketModalStatusFilter, search = ticketModalSearchQuery) => {
    if (!type) return;
    try {
      setModalTicketsLoading(true);
      const params = {
        page: 0,
        size: 20,
        asRequester: true
      };
      const statusParam = getModalStatusParam(type, statusFilter);
      if (statusParam) {
        params.status = statusParam;
      }
      if (search && search.trim()) {
        params.search = search.trim();
      }

      const response = await ticketService.getMyTickets(params);
      const responseData = response?.data;
      const paginatedData = responseData?.data;
      let list = (paginatedData?.content || []).map(transformTicket);
      if (type === 'inProgress') {
        list = list.filter((t) => {
          const s = (t.status || '').toLowerCase();
          return s === 'in-progress' || s === 'in progress' || s === 'assigned' || s === 'inprogress';
        });
      }

      setTickets(list);
    } catch (err) {
      console.error('Error loading tickets for modal:', err);
      toast.error('Failed to load tickets');
      setTickets([]);
    } finally {
      setModalTicketsLoading(false);
    }
  };

  useEffect(() => {
    if (!showTicketModal || !selectedTicketType) return;
    const delay = ticketModalSearchQuery?.trim() ? 400 : 0;
    const timer = setTimeout(() => {
      loadModalTickets(selectedTicketType, ticketModalStatusFilter, ticketModalSearchQuery);
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTicketModal, selectedTicketType, ticketModalStatusFilter, ticketModalSearchQuery]);

  const handleTicketCardClick = (type) => {
    setSelectedTicketType(type);
    setTicketModalSearchQuery('');
    setTicketModalStatusFilter('all');
    setTickets([]);
    setShowTicketModal(true);
  };

  const getFilteredTickets = () => tickets;

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'urgent': return 'bg-red-800 text-red-50';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      setKbModalSeedQuery(q);
      setShowSearchKBModal(true);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FCFB] text-[#0B2530]">
      {/* Header is provided by PortalHomeLayout */}

      {/* Hero Section (from PortalHome + PortalIndex) */}
      <section className="relative py-8 md:py-12 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/background-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(2px)',
            opacity: 0.3
          }}
        ></div>
        {/* Color overlay for better text visibility */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-b from-[#E0F8F3] via-white/70 to-transparent" />

        <div className="text-center space-y-5 relative z-10 py-4 md:py-6 px-4">
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-[#0B2530] leading-tight">
              Welcome to Support Portal
            </h1>
            <p className="text-base md:text-lg text-[#4A5B63] max-w-2xl mx-auto">
              Your one-stop solution for all support needs.
            </p>
          </div>

          <div className="max-w-2xl mx-auto px-4">
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search articles, tickets, or documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11 text-sm bg-white border shadow-sm rounded-lg"
                style={{ borderColor: '#E2E8F0' }}
              />
              <Button 
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 px-4 text-sm"
                style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
              >
                Search
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-2">
              Try: "reset password", "ticket status", "getting started"
            </p>
          </div>
        </div>
      </section>

      {/* Quick Actions Section - Ticket Overview (from PortalDashboard) */}
      <section className="py-6 md:py-10 relative bg-gradient-to-b from-[#F8FCFB] to-white px-4">
        <div>
          <div className="text-center mb-6 md:mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B2530] mb-2">Your Ticket Overview</h2>
            <p className="text-sm md:text-base text-[#4A5B63]">Quick access to your support tickets</p>
          </div>

          {/* Quick Stats - Clickable Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5 mb-6">
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-[#006600]"
              onClick={() => handleTicketCardClick('total')}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-[#4A5B63]">Total Tickets</p>
                    {loading ? (
                      <div className="h-6 w-12 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                      <p className="text-xl md:text-2xl font-bold text-[#1A202C]">{ticketOverview.total}</p>
                    )}
                  </div>
                  <Ticket className="h-6 w-6 md:h-8 md:w-8 text-[#006600]" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-[#006600]"
              onClick={() => handleTicketCardClick('open')}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-[#4A5B63]">Open Tickets</p>
                    {loading ? (
                      <div className="h-6 w-12 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                      <p className="text-xl md:text-2xl font-bold text-[#1A202C]">{ticketOverview.open}</p>
                    )}
                  </div>
                  <AlertCircle className="h-6 w-6 md:h-8 md:w-8 text-[#006600]" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-[#006600]"
              onClick={() => handleTicketCardClick('inProgress')}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-[#4A5B63]">In Progress</p>
                    {loading ? (
                      <div className="h-6 w-12 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                      <p className="text-xl md:text-2xl font-bold text-[#1A202C]">{ticketOverview.inProgress}</p>
                    )}
                  </div>
                  <Clock className="h-6 w-6 md:h-8 md:w-8 text-[#006600]" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-[#006600]"
              onClick={() => handleTicketCardClick('resolved')}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-[#4A5B63]">Resolved</p>
                    {loading ? (
                      <div className="h-6 w-12 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                      <p className="text-xl md:text-2xl font-bold text-[#1A202C]">{ticketOverview.resolved}</p>
                    )}
                  </div>
                  <CheckCircle className="h-6 w-6 md:h-8 md:w-8 text-[#006600]" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-[#006600]"
              onClick={() => handleTicketCardClick('closed')}
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-[#4A5B63]">Closed</p>
                    {loading ? (
                      <div className="h-6 w-12 bg-gray-200 animate-pulse rounded mt-1"></div>
                    ) : (
                      <p className="text-xl md:text-2xl font-bold text-[#1A202C]">{ticketOverview.closed}</p>
                    )}
                  </div>
                  <FileText className="h-6 w-6 md:h-8 md:w-8 text-[#006600]" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions (from PortalDashboard) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Plus className="h-5 w-5" />
                <span>Quick Actions</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button 
                  onClick={() => setShowCreateTicketModal(true)}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-all duration-200 text-left group"
                >
                  <Plus className="h-6 w-6 text-blue-600 mb-2 group-hover:scale-110 transition-transform" />
                  <h3 className="font-medium text-gray-900">Create Ticket</h3>
                  <p className="text-sm text-gray-600">Submit a new support request</p>
                </button>

                <button 
                  onClick={() => setShowMyTicketsModal(true)}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-all duration-200 text-left group"
                >
                  <Ticket className="h-6 w-6 text-blue-600 mb-2 group-hover:scale-110 transition-transform" />
                  <h3 className="font-medium text-gray-900">View My Tickets</h3>
                  <p className="text-sm text-gray-600">Check status of your tickets</p>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How It Works Section (from PortalHome) */}
      <section className="py-8 md:py-12 relative bg-white">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/background-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(2px)',
            opacity: 0.2
          }}
        ></div>
        {/* Color overlay for better text visibility */}
        <div className="absolute inset-0 bg-white/70"></div>
        <div className="relative z-10 px-4">
          <div className="text-center mb-6 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-3 text-[#0B2530]">How Our Ticketing System Works</h2>
            <p className="text-[#4A5B63] max-w-2xl mx-auto">
              From submission to resolution, we make it easy to get the support you need
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative group">
                <Card className="h-full hover:shadow-luxury transition-smooth border-2 hover:border-primary/50 gradient-card overflow-hidden">
                  <CardContent className="p-5 space-y-3 relative">
                    <div className="absolute top-0 right-0 w-32 h-32 gradient-accent opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity"></div>
                    <div className="flex items-center gap-3 relative z-10">
                      <div className="gradient-primary text-primary-foreground font-bold text-xl rounded-full w-12 h-12 flex items-center justify-center shadow-glow">
                        {item.step}
                      </div>
                      <div className="gradient-accent rounded-lg p-2.5 shadow-elegant">
                        <item.icon className="h-6 w-6 text-primary-foreground" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold relative z-10 text-[#0B2530]">{item.title}</h3>
                    <p className="text-sm text-[#617079] relative z-10 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-0 transform -translate-y-1/2 text-primary">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid (from PortalHome) */}
      <section className="py-8 md:py-12 relative bg-gradient-to-b from-[#F8FCFB] to-white">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/background-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(2px)',
            opacity: 0.2
          }}
        ></div>
        {/* Color overlay for better text visibility */}
        <div className="absolute inset-0 bg-[#F8FCFB]/80"></div>
        <div className="relative z-10 px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-3 text-[#0B2530]">Why Choose Our Support Portal</h2>
            <p className="text-[#4A5B63]">
              We're committed to providing exceptional support experience
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-luxury transition-smooth border-2 hover:border-primary/30 group gradient-card overflow-hidden relative">
                <div className="absolute inset-0 gradient-primary opacity-0 group-hover:opacity-5 transition-opacity"></div>
                <CardContent className="p-6 md:p-8 space-y-4 relative z-10">
                  <div className={`inline-flex p-5 bg-muted rounded-2xl shadow-elegant group-hover:scale-110 transition-transform ${feature.color}`}>
                    <feature.icon className="h-8 w-8 md:h-10 md:w-10" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0B2530]">{feature.title}</h3>
                  <p className="text-sm text-[#617079] leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Action Cards Section (from PortalHome) */}
      <section className="py-8 md:py-12 relative bg-white">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/background-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(2px)',
            opacity: 0.25
          }}
        ></div>
        {/* Color overlay for better text visibility */}
        <div className="absolute inset-0 bg-white/65"></div>
        <div className="relative z-10 px-4 max-w-6xl mx-auto w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-stretch">
            {/* Submit a Ticket — single container */}
            <Card
              className="group flex h-full flex-col rounded-2xl border-2 border-[#1a6b3c] bg-white shadow-sm transition-all hover:border-[#006600] hover:shadow-lg cursor-pointer"
              onClick={() => navigate('/portal/tickets/create')}
            >
              <CardContent className="flex h-full flex-col p-6 md:p-8 pt-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="rounded-xl bg-[#006600] p-3 shrink-0">
                    <Plus className="h-7 w-7 text-white" />
                  </div>
                  <CardTitle className="text-xl md:text-2xl font-bold text-[#0B2530] m-0">
                    Submit a Ticket
                  </CardTitle>
                </div>
                <CardDescription className="text-base text-[#617079] mb-6">
                  Need personalized assistance? Create a support ticket and our team will get back to you.
                </CardDescription>
                <div className="space-y-4 flex-1">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-[#006600] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-[#0B2530]">Secure & Private</p>
                      <p className="text-sm text-[#617079]">Your data is encrypted and protected</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-[#006600] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-[#0B2530]">Fast Response</p>
                      <p className="text-sm text-[#617079]">Average response time under 2 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#006600] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-[#0B2530]">Track Progress</p>
                      <p className="text-sm text-[#617079]">Real-time updates on your ticket status</p>
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  className="w-full mt-6 gradient-primary text-primary-foreground hover:opacity-90"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/portal/tickets/create');
                  }}
                >
                  Create New Ticket
                </Button>
              </CardContent>
            </Card>

            {/* Knowledge Base — informational only (use sidebar menu to open KB) */}
            <Card
              className="flex h-full flex-col rounded-2xl border-2 border-[#8B2635] bg-white shadow-sm"
            >
              <CardContent className="flex h-full flex-col p-6 md:p-8 pt-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="rounded-xl bg-[#8B2635] p-3 shrink-0">
                    <BookOpen className="h-7 w-7 text-white" />
                  </div>
                  <CardTitle className="text-xl md:text-2xl font-bold text-[#0B2530] m-0">
                    Knowledge Base
                  </CardTitle>
                </div>
                <CardDescription className="text-base text-[#617079] mb-6">
                  Find answers instantly in our comprehensive collection of articles and guides.
                </CardDescription>
                <div className="space-y-4 flex-1">
                  <div className="flex items-start gap-3">
                    <Search className="h-5 w-5 text-[#8B2635] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-[#0B2530]">Easy Search</p>
                      <p className="text-sm text-[#617079]">Find solutions with powerful search</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-[#8B2635] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-[#0B2530]">Step-by-Step Guides</p>
                      <p className="text-sm text-[#617079]">Detailed tutorials and documentation</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Zap className="h-5 w-5 text-[#8B2635] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-[#0B2530]">Instant Access</p>
                      <p className="text-sm text-[#617079]">Available 24/7, no waiting required</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-[#8B2635] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-[#0B2530]">User Guide</p>
                      <p className="text-sm text-[#617079]">For step by step guides</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>


      {/* My Tickets CTA (from PortalHome) */}
      <section className="py-8 md:py-12 relative overflow-hidden bg-white">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/background-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(2px)',
            opacity: 0.3
          }}
        ></div>
        {/* Color overlay for better text visibility */}
        <div className="absolute inset-0 bg-white/60"></div>
        <div className="relative z-10 px-4">
          <Card className="bg-[#0B2530] text-white overflow-hidden shadow-2xl shadow-[#071136]/40 border-0 relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl"></div>
            <CardContent className="p-8 md:p-12 lg:p-16 text-center space-y-6 md:space-y-8 relative z-10">
              <div className="inline-flex p-4 md:p-6 bg-white/10 backdrop-blur-sm rounded-3xl shadow-glow animate-float">
                <FileText className="h-12 w-12 md:h-16 md:w-16" />
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold drop-shadow-lg">Already Submitted a Ticket?</h2>
              <p className="text-lg md:text-xl lg:text-2xl opacity-95 max-w-2xl mx-auto leading-relaxed text-white/80">
                View and manage all your support tickets in one place. Track progress, add comments, and get real-time updates.
              </p>
              <Button
                size="lg"
                className="mt-6 bg-white text-white hover:bg-gray-100 px-8 md:px-12 py-4 md:py-6 text-base md:text-lg font-semibold shadow-elegant hover:shadow-glow transition-all hover:scale-105"
                onClick={() => navigate('/portal/tickets')}
              >
                View My Tickets
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer is provided by PortalHomeLayout */}

      {/* Modals (from PortalDashboard) */}
      {/* Ticket Overview Modal */}
      <Modal
        isOpen={showTicketModal}
        onClose={() => {
          setShowTicketModal(false);
          setSelectedTicketType(null);
          setTicketModalSearchQuery('');
          setTicketModalStatusFilter('all');
          setTickets([]);
        }}
        title={selectedTicketType === 'total' ? 'All Tickets' : 
               selectedTicketType === 'open' ? 'Open Tickets' :
               selectedTicketType === 'resolved' ? 'Resolved Tickets' :
               selectedTicketType === 'inProgress' ? 'In Progress Tickets' :
               selectedTicketType === 'closed' ? 'Closed Tickets' : 'Tickets'}
        size="xl"
      >
        <div className="space-y-4">
          {/* Search and Filter Bar */}
          <div className={`flex flex-col sm:flex-row gap-3 ${selectedTicketType === 'total' ? '' : 'flex-row'}`}>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search tickets by title, description, or ID..."
                value={ticketModalSearchQuery}
                onChange={(e) => setTicketModalSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {selectedTicketType === 'total' && (
              <Select value={ticketModalStatusFilter} onValueChange={setTicketModalStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          
          {modalTicketsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderBottomColor: '#006600' }}></div>
              <p className="mt-2" style={{ color: '#4A5B63' }}>Loading tickets...</p>
            </div>
          ) : getFilteredTickets().length === 0 ? (
            <div className="text-center py-8">
              <Ticket className="h-12 w-12 mx-auto mb-4" style={{ color: '#94A3AB' }} />
              <p style={{ color: '#4A5B63' }}>No tickets found</p>
            </div>
          ) : (
            <div className="space-y-3 pr-2">
              {getFilteredTickets().map((ticket) => (
                <Card key={ticket.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold" style={{ color: '#0B2530' }}>{ticket.ticketNumber || ticket.id || ticket.ticketId}</h3>
                          <Badge className={getStatusColor(
                            isCustomerViewer
                              ? getCustomerFacingTicketStatusStyleKey(ticket)
                              : ticket.status
                          )}>
                            {isCustomerViewer
                              ? getCustomerFacingTicketStatusLabel(ticket)
                              : ticket.status}
                          </Badge>
                          {ticket.priority && (
                            <Badge className={getPriorityColor(ticket.priority)}>
                              {ticket.priority}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium mb-1" style={{ color: '#0B2530' }}>
                          {ticket.subject || ticket.title}
                        </p>
                        {ticket.description && (
                          <p className="text-sm mb-2 line-clamp-2" style={{ color: '#4A5B63' }}>
                            {ticket.description}
                          </p>
                        )}
                        {/* {(ticket.countryName || ticket.countyName || ticket.subCountyName || ticket.wardName) && (
                          <p className="text-xs mb-2" style={{ color: '#8A9AA2' }}>
                            📍 {[
                              ticket.countryName,
                              ticket.countyName,
                              ticket.subCountyName,
                              ticket.wardName
                            ].filter(Boolean).join(' > ')}
                          </p>
                        )} */}
                        <div className="flex items-center gap-4 text-xs" style={{ color: '#8A9AA2' }}>
                          {ticket.createdAt && (
                            <span>Created: {formatDate(ticket.createdAt)}</span>
                          )}
                          {ticket.lastUpdate && (
                            <span>Updated: {formatDate(ticket.lastUpdate)}</span>
                          )}
                          {ticket.assignedTo && (
                            <span>Assigned to: {ticket.assignedTo}</span>
                          )}
                        </div>
                      </div>
                      {/* <Button variant="outline" size="sm" className="ml-4">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button> */}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Create Ticket Modal */}
      <Modal
        isOpen={showCreateTicketModal}
        onClose={() => setShowCreateTicketModal(false)}
        title="Create New Ticket"
        size="full"
      >
        <CreateTicketForm 
          onSuccess={() => {
            setShowCreateTicketModal(false);
            loadTicketOverview();
          }}
          onClose={() => setShowCreateTicketModal(false)}
        />
      </Modal>

      {/* Search KB Modal */}
      <Modal
        isOpen={showSearchKBModal}
        onClose={() => {
          setShowSearchKBModal(false);
          setKbModalSeedQuery('');
        }}
        title="Search Knowledge Base"
        size="full"
      >
        <div className="max-h-[85vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <KnowledgeSearch
            key={kbModalSeedQuery || 'kb-modal-default'}
            initialQuery={kbModalSeedQuery}
            runSearchOnMount={Boolean(kbModalSeedQuery?.trim())}
          />
        </div>
      </Modal>

      {/* My Tickets Modal */}
      <Modal
        isOpen={showMyTicketsModal}
        onClose={() => setShowMyTicketsModal(false)}
        title="My Tickets"
        size="full"
      >
        <MyTickets onCreateTicket={() => {
          setShowMyTicketsModal(false);
          setShowCreateTicketModal(true);
        }} />
      </Modal>

      {/* Knowledge Base Modal */}
      <Modal
        isOpen={showKnowledgeBaseModal}
        onClose={() => setShowKnowledgeBaseModal(false)}
        title="Knowledge Base"
        size="full"
      >
        <KnowledgeBaseAccess />
      </Modal>
      {/* <LandingChatbot email={userEmail} /> */}
    </div>
  );
};

export default UnifiedPortalDashboard;

